import React from "react";
import {
  Avatar,
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Divider,
} from "@mui/material";

const OfferDetailsView = ({ data }) => {
  if (!data) return <Typography>No data available</Typography>;

  return (
    <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
      {/* Title */}
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        {data.title}
      </Typography>

      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        {data.short_desc}
      </Typography>

      <Divider sx={{ my: 2 }} />

      {/* Image */}
      {data.images?.length > 0 && (
        <Stack direction="row" spacing={2} alignItems="center">
          {data.images.map((img) => (
            <Avatar
              key={img._id}
              src={img.url}
              alt={img.alt}
              variant="rounded"
              sx={{ width: 100, height: 100 }}
            />
          ))}
        </Stack>
      )}

      {/* Price */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6">Price</Typography>
        <Typography>
          Before: {data.price?.before} {data.price?.currency}
        </Typography>
        <Typography>
          After: {data.price?.after} {data.price?.currency}
        </Typography>
      </Box>

      {/* Inventory */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6">Inventory</Typography>
        <Typography>Max Redemptions: {data.inventory?.max_redemptions}</Typography>
        <Typography>Remaining: {data.inventory?.remaining}</Typography>
        <Typography>Per User / Day: {data.inventory?.per_user_per_day}</Typography>
      </Box>

      {/* Analytics */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6">Analytics</Typography>
        <Typography>Views: {data.analytics?.views}</Typography>
        <Typography>Clicks: {data.analytics?.clicks}</Typography>
        <Typography>Saves: {data.analytics?.saves}</Typography>
        <Typography>Redemptions: {data.analytics?.redemptions}</Typography>
      </Box>

      {/* Provider */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6">Provider</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          {data.provider?.logo?.url && (
            <Avatar src={data.provider.logo.url} alt="logo" />
          )}
          <Box>
            <Typography>{data.provider?.providerName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {data.provider?.address}, {data.provider?.region}
            </Typography>
            <Typography variant="body2">
              Lat: {data.provider?.latitude}, Lng: {data.provider?.longitude}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Categories */}
      {data.categories?.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6">Categories</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {data.categories.map((cat) => (
              <Box
                key={cat._id}
                sx={{
                  border: "1px solid #ddd",
                  borderRadius: 2,
                  px: 1.5,
                  py: 0.5,
                  bgcolor: "#f9f9f9",
                }}
              >
                <Typography variant="body2">
                  {cat.Name} ({cat.type})
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* Voucher */}
      {data.voucherId && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6">Voucher</Typography>
          <Typography>Title: {data.voucherId.title}</Typography>
          <Typography>Quantity: {data.voucherId.quantity}</Typography>
        </Box>
      )}

      {/* Other Fields in Table */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Details
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Field</TableCell>
                <TableCell>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                ["Type", data.type],
                ["Terms", data.terms],
                ["Sponsored Label", data.sponsored_label],
                ["Priority", data.priority],
                ["Active", data.active ? "Yes" : "No"],
                ["Sponsored", data.sponsored ? "Yes" : "No"],
                ["Deleted", data.isDeleted ? "Yes" : "No"],
                ["Relevance Score", data.relevance_score],
                ["Popularity", data.popularity],
                ["Taken", data.taken ? "Yes" : "No"],
                ["Valid From", new Date(data.valid_from).toLocaleString()],
                ["Valid To", new Date(data.valid_to).toLocaleString()],
                ["Created At", new Date(data.createdAt).toLocaleString()],
                ["Updated At", new Date(data.updatedAt).toLocaleString()],
              ].map(([key, value]) => (
                <TableRow key={key}>
                  <TableCell>{key}</TableCell>
                  <TableCell>{value?.toString() ?? "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Paper>
  );
};

export default OfferDetailsView;
